/* eslint-disable no-case-declarations */
import React, { useState, useEffect } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import IDHelpers from '../../../../../../../core/helpers/IDHelper';
import { Filterable } from '../../../../../../components/Select/filterable';
import {
  ContainerSelect,
  OptionsContainer,
  Label,
  ShowDefaultError
} from './styles';
import { Icon } from '../../../../../../components';

interface ISelect {
  optionsList: any[];
  value: any;
  placeholder: string;
  nameLabel?: string;
  infoText?: string;
  setFieldValue: ({ name, value }: any) => void;
  name: string;
  validateOnStart?: boolean;
  validateForm: number;
  isFilterable?: boolean;
  dependencies?: any;
  dependients?: any;
  top?: boolean;
  isDisabled?: boolean;
  fontWeight?: boolean;
}

const Select = (props: ISelect) => {
  const {
    optionsList,
    value,
    placeholder,
    nameLabel,
    infoText = '',
    setFieldValue,
    name,
    validateOnStart = false,
    validateForm,
    isFilterable = false,
    dependencies = {},
    dependients = {},
    top,
    isDisabled = false,
    fontWeight
  } = props;

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(0);
  const [filterOptions, setFilterOptions]: any = useState<any>([]);
  const [errorText, setErrorText] = useState('');

  const toggleOptions = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };
  const existsDependencies = (object: any) => Object.keys(object).length !== 0;
  const isComplete = (object: any) => {
    const finder = Object.values(object).find(
      (val: any) => val === null || val === ''
    );
    return typeof finder === 'undefined';
  };
  const enableOptionsIfExistsDependencies = () =>
    !existsDependencies(dependencies) ||
    (existsDependencies(dependencies) && isComplete(dependencies));

  const setSelectedThenCloseDropdown = (index: any, option: any) => {
    setSelectedOption(index);
    setIsOptionsOpen(false);
    setFieldValue({
      name,
      value: option.id?.toString()
    });
    setErrorText('');
    setFilterOptions(optionsList);
  };

  const handleKeyDown = (index: any, option: any) => (e: any) => {
    switch (e.key) {
      case ' ':
      case 'SpaceBar':
      case 'Enter': {
        e.preventDefault();
        setSelectedThenCloseDropdown(index, option);
        break;
      }
      case 'ArrowDown': {
        const nextOption = e.target.nextSibling;
        nextOption?.focus();
        e.preventDefault();
        break;
      }
      case 'ArrowUp': {
        const prevOption = e.target.previousSibling;
        prevOption?.focus();
        e.preventDefault();
        break;
      }
      default:
        break;
    }
  };

  const updateFilterOptions = (filter: any) => {
    if (filter.length >= 3) {
      const newFilteredOptions = [...optionsList].filter((option) =>
        option.description.toLowerCase().includes(filter.trim().toLowerCase())
      );
      if (newFilteredOptions.length > 0) {
        setFilterOptions(newFilteredOptions);
      }
    } else {
      setFilterOptions(optionsList);
    }
  };

  const getSelectedText = () => {
    const optionSelected = optionsList?.find(
      (o: any) => o.id?.toString() === value?.toString()
    );
    const text = (value && optionSelected?.description) || placeholder;
    return text;
  };

  useEffect(() => {
    setFilterOptions(optionsList);
  }, [optionsList]);

  useEffect(() => {
    if ((validateOnStart || validateForm > 0) && !value) {
      setErrorText('Dato pendiente');
    }
  }, [validateOnStart, validateForm]);

  useEffect(() => {
    if (value || existsDependencies(dependients)) {
      const newValues: any = [];
      Object.keys(dependients).forEach((prop: any) => {
        newValues.push({
          name: prop,
          value: null
        });
      });
      setFieldValue(newValues);
    }
  }, [value]);

  return (
    <ContainerSelect
      data-testid="cmp-bse-form-select"
      color={value ? '#283044' : '#7a959f'}
    >
      {nameLabel && (
        <div
          style={{
            fontSize: '14px',
            fontWeight: fontWeight ? '400' : '700',
            lineHeight: '20px',
            letterSpacing: '0px',
            textAlign: 'left',
            paddingBottom: '16px',
            color: '#161d1f'
          }}
        >
          {nameLabel}
        </div>
      )}
      <OutsideClickHandler onOutsideClick={() => setIsOptionsOpen(false)}>
        <button
          data-testid="input-toggle-select"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOptionsOpen}
          className={`${errorText && 'inputError'} ${
            isOptionsOpen ? 'expanded' : ''
          }`}
          onClick={
            enableOptionsIfExistsDependencies() ? toggleOptions : () => {}
          }
          disabled={isDisabled}
          style={{ height: 40 }}
        >
          <Label color={value ? '#161d1f' : '#4f6168'}>
            <p className="overflow-ellipsis">{getSelectedText()}</p>
          </Label>
        </button>
        {enableOptionsIfExistsDependencies() && (
          <OptionsContainer
            top={top}
            className={`${errorText && top && 'fixTop'}`}
          >
            {isFilterable && isOptionsOpen && (
              <Filterable
                valueSelected={value}
                onChange={updateFilterOptions}
              />
            )}
            {filterOptions && (
              <ul
                className={`options ${isOptionsOpen ? 'show' : ''}`}
                role="listbox"
                aria-activedescendant={filterOptions[selectedOption]}
                tabIndex={-1}
                data-testid="cmp-bse-form-select-options"
              >
                {filterOptions.length ? (
                  filterOptions.map((option: any, index: any) => (
                    <li
                      data-testid="cmp-select-li-item"
                      key={IDHelpers.generateID()}
                      id={option.id}
                      role="option"
                      aria-selected={
                        option.id?.toString() === value?.toString()
                      }
                      tabIndex={0}
                      onKeyDown={handleKeyDown(index, option)}
                      onClick={() => {
                        setSelectedThenCloseDropdown(index, option);
                      }}
                    >
                      {option.description}
                    </li>
                  ))
                ) : (
                  <li>No se encontraron opciones.</li>
                )}
              </ul>
            )}
          </OptionsContainer>
        )}
      </OutsideClickHandler>
      {errorText ? (
        <ShowDefaultError>
          <div className="errorText">
            <Icon name="alert-triangle" size={12} />
            {errorText}
          </div>
        </ShowDefaultError>
      ) : (
        infoText && <Label color="#5B36F2"> {infoText} </Label>
      )}
    </ContainerSelect>
  );
};

Select.defaultProps = {
  top: false
};

export default Select;
